import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryParams, useNavigate } from 'raviger';
import { Box, Button, Typography, TextField, InputAdornment, IconButton, Container, FormHelperText } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Animated } from 'react-animated-css';
import { useFormik } from 'formik';
import * as yup from 'yup';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import axios from 'axios';
import { feathersClient } from 'api/client';

import appBG from '../../assets/appBG.jpg';
import forgotPass from '../../assets/forgotPass.svg';
import mobileResetPass from '../../assets/mobileResetPass.svg';
import line from '../../assets/line.svg';
import zofimMobilePass from '../../assets/zofimMobilePass.svg';
import { MessageBox } from '../../Components/Common/MessageBox';
import { VBox, HBox } from 'assets/SharedComponents';

const useStyles = makeStyles(() => ({
    background: {
        backgroundImage: `url(${appBG})`,
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundAttachment: 'fixed',
        minHeight: "100vh"
    },
    buttonGreen: {
        color: '#3a7735 !important',
        fontSize: '1.2rem !important',
        fontWeight: 'bold !important',
        backgroundColor: '#ffffff !important',
        borderRadius: '2rem !important',
        boxShadow: '0 8px 9px rgb(125 125 125 / 40%)',
        textTransform: "capitalize !important",
        width: "15rem !important",
        border: "1px solid !important",
        marginTop: "1rem !important"
    },
    textFieldStyle: {
        '& .MuiInputLabel-root': {
            color: '#ffffff !important',
        },
        '& .MuiInputLabel-root.Mui-focused': {
            color: '#ffffff !important',
        },
        '& .MuiOutlinedInput-root': {
            borderRadius: '0.7rem'
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: "#ffffff !important"
        }
    },
    text: {
        color: '#ffffff !important',
        fontWeight: "bold !important",
        fontSize: "0.9rem !important"
    },
    text1: {
        color: '#3a7735 !important',
        fontWeight: "bold !important"
    }
}));

const Auth0ResetPasswordPage = ({ token }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const urlParams = useQueryParams();
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState(false);
    const [disabled, setDisabled] = useState(false);
    const [success, setSuccess] = useState(false);
    const [messageBoxState, setMessageBox] = useState({ open: false });


    const validationSchema = yup.object({
        password: yup
            .string()
            .required(t("mandatoryField"))
            .min(8, t("correctPasswordPlease"))
            .matches(/^(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z\d]).{8,}$/, t("correctPasswordPlease"))
    });

    const formik = useFormik({
        initialValues: {
            password: ''
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            try {
                console.log('🔐 Resetting password...');

                // Call YOUR backend to complete password reset
                await feathersClient.service('complete-password-reset-auth0').create({
                    token: token,
                    newPassword: values.password
                });

                console.log('✅ Password reset successful');
                setSuccess(true);

            } catch (error) {
                console.error('❌ Password reset failed:', error);
                setDisabled(true);

                const errorMessage = error.message?.includes('expired')
                    ? t("expLink")
                    : error.message?.includes('strength')
                        ? t("correctPasswordPlease")
                        : t("generalError");

                setMessageBox({
                    open: true,
                    title: (
                        <Box display="flex" alignItems="center">
                            <ErrorRoundedIcon />
                            <Typography variant="h5" paddingLeft="1rem">
                                {t("note")}
                            </Typography>
                        </Box>
                    ),
                    content: (
                        <Box textAlign="center">
                            <Typography className={classes.text1}>
                                {errorMessage}
                            </Typography>
                        </Box>
                    ),
                    actions: (
                        <Button className={classes.buttonGreen} onClick={() => navigate("/")}>
                            {t("backToLogin1")}
                        </Button>
                    )
                });
            }
        }
    });

    return (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" className={classes.background}>
            {!success ? (
                <Container sx={{ padding: "1.5rem !important", textAlign: "center" }}>
                    <img
                        style={{ paddingLeft: "1.5rem", marginBottom: "1.5rem" }}
                        width="fit-content"
                        height="auto"
                        alt="reset password"
                        src={forgotPass}
                    />
                    <VBox sx={{ marginBottom: "1.1rem" }}>
                        <Typography className={classes.text} sx={{ fontSize: "1.5rem !important" }}>
                            {t("emailAddPass")}
                        </Typography>
                        <Typography className={classes.text}>
                            {t("emailAddPass1")}
                        </Typography>
                    </VBox>
                    <form onSubmit={formik.handleSubmit}>
                        <HBox>
                            <TextField
                                className={classes.textFieldStyle}
                                label={t('choosePassword2')}
                                disabled={disabled}
                                type={showPassword ? "text" : "password"}
                                style={{ width: "100%" }}
                                id="password"
                                value={formik.values.password}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.password && Boolean(formik.errors.password)}
                                helperText={formik.touched.password && formik.errors.password}
                                variant="outlined"
                                InputProps={{
                                    sx: { color: "#ffffff" },
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setShowPassword(!showPassword)}
                                            >
                                                {showPassword ? (
                                                    <VisibilityIcon sx={{ color: "#ffffff" }} />
                                                ) : (
                                                    <VisibilityOffIcon sx={{ color: "#ffffff" }} />
                                                )}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </HBox>
                        <FormHelperText style={{ fontSize: "0.8rem", color: "#ffffff" }}>
                            {t("passwordRule1")}
                        </FormHelperText>
                        <FormHelperText style={{ fontSize: "0.8rem", color: "#ffffff" }}>
                            {t("passwordRule2")}
                        </FormHelperText>
                        <Button className={classes.buttonGreen} type="submit" disabled={disabled}>
                            {t("changePassowrd")}
                        </Button>
                    </form>
                </Container>
            ) : (
                <>
                    <Animated animationIn="fadeIn" animationInDuration={1800} isVisible={true}>
                        <Typography sx={{ color: "#ffffff", fontSize: "1.3rem" }}>
                            {t("passwordChanged")}
                        </Typography>
                    </Animated>
                    <Animated animationIn="slideInUp" animationInDuration={1200} isVisible={true}>
                        <img src={mobileResetPass} alt="success" style={{ marginTop: "1rem" }} />
                    </Animated>
                    <Animated animationIn="slideInUp" animationInDuration={1200} isVisible={true}>
                        <img src={line} alt="line" style={{ marginTop: "1rem" }} />
                    </Animated>
                    <Animated animationIn="fadeIn" animationInDuration={1800} animationInDelay={1801} isVisible={true}>
                        <Typography sx={{ color: "#ffffff", fontSize: "1.3rem" }}>
                            {t("nowGoBack1")}
                        </Typography>
                        <Typography sx={{ color: "#ffffff", fontSize: "1.3rem", textAlign: "center" }}>
                            {t("toBrowser")}
                        </Typography>
                    </Animated>
                    <Animated animationIn="slideInUp" animationInDuration={1200} animationInDelay={1203} isVisible={true}>
                        <img src={zofimMobilePass} alt="mobile" style={{ marginTop: "1rem" }} />
                    </Animated>
                    <Animated animationIn="slideInUp" animationInDuration={1200} animationInDelay={1203} isVisible={true}>
                        <img src={line} alt="line" style={{ marginTop: "1rem" }} />
                    </Animated>
                    <Animated animationIn="fadeIn" animationInDuration={1200} animationInDelay={2405} isVisible={true}>
                        <Typography sx={{ color: "#ffffff", fontSize: "1.3rem" }}>
                            {t("loginAgain")}
                        </Typography>
                    </Animated>
                    <Animated animationIn="fadeIn" animationInDuration={700} animationInDelay={3600} isVisible={true}>
                        <Button className={classes.buttonGreen} onClick={() => navigate("/")}>
                            {t("backToLogin1")}
                        </Button>
                    </Animated>
                </>
            )}
            <MessageBox messageBoxState={messageBoxState} setMessageBox={setMessageBox} />
        </Box>
    );
};

export default Auth0ResetPasswordPage;